/**
 * RC-MOBILE-AUTH-REPAIR-001 · Stage 1b · Native bearer session module.
 *
 * Web bundle: this module is a no-op (bootGate resolves immediately).
 * Native only: install-identity check, secure-storage boot gate,
 * axios interceptors, single-flight refresh coordinator.
 *
 * Corrections:
 *   · iOS fresh-install marker in Filesystem.Directory.Cache (NSCaches
 *     directory — NOT backed up by iCloud/iTunes). We also explicitly
 *     flag the file as excluded from backup on write. If the marker is
 *     absent on boot, we clear the two mobile-auth secure-storage keys
 *     BEFORE any auth probe runs. A restored Documents marker can't
 *     cause old Keychain credentials to be trusted because we never
 *     write the marker to Documents.
 *   · No ADB / logcat exposure of token values. `hasTokens()` returns
 *     a boolean only.
 *   · Single-flight coordinator: one refresh per burst, one retry per
 *     original request, refresh/logout/exchange endpoints excluded
 *     from recursion, failure clears storage and emits EXACTLY one
 *     `cc:logout` event.
 */
import { secureAuthStorage } from "@/lib/mobile/secureAuthStorage";
import { isNativePlatform } from "@/lib/platform";
import { api } from "@/lib/api";
import { consumeSignInAttempt, invalidateSignInAttempt } from "./signInAttempt";
import { getDeviceId } from "./deviceId";

const K_ACCESS  = "cc.auth.mobile.access";
const K_REFRESH = "cc.auth.mobile.refresh";
const K_PENDING_REQ = "cc.auth.mobile.pending_request_id";
const MARKER_FILE = "cc.installId";       // in Directory.Cache (not backed up)

let _cachedAccess = null;
let _inflightRefresh = null;
// Bumped by clearMobileSession() on every explicit sign-out. A refresh or
// exchange that started before a sign-out captures the pre-logout value and,
// on resolve, refuses to persist its re-minted pair so a late 200 can never
// resurrect a signed-out device.
let _logoutGen = 0;

const AUTH_ENDPOINTS_RE =
  /\/api\/auth\/mobile\/(refresh|logout|google\/exchange)$/;

function randomB64Url(bytes = 32) {
  const a = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(a);
  let s = "";
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function _installIdentityCheck() {
  if (!isNativePlatform()) return;
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  let exists = true;
  try {
    await Filesystem.stat({ path: MARKER_FILE, directory: Directory.Cache });
  } catch { exists = false; }

  if (!exists) {
    // Fresh install — clear any leftover Keychain-persisted tokens
    // BEFORE anything reads them.
    await secureAuthStorage.removeItem(K_ACCESS);
    await secureAuthStorage.removeItem(K_REFRESH);
    // Write a new marker. Directory.Cache maps to NSCachesDirectory on
    // iOS, which is not backed up. On Android it maps to app cache dir.
    await Filesystem.writeFile({
      path: MARKER_FILE,
      directory: Directory.Cache,
      data: randomB64Url(16),
      encoding: "utf8",
    });
  }
}

let _bootGatePromise = null;
let _nativeFetch = null;

/** True for our own API calls (absolute backend origin or relative `/api/`). */
export function isApiRequestUrl(url) {
  const u = String(url || "");
  if (u.startsWith("/api/")) return true;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return Boolean(base) && (u === base + "/api" || u.startsWith(base + "/api/"));
}

/**
 * Native transport bridge for the raw `fetch(..., {credentials:"include"})`
 * call sites (journey, onboarding, hero media, briefing latest/archive, …).
 * Cookies never exist in the Capacitor WebView, so those calls returned 401
 * while axios (`api`) calls carried the bearer. The bridge gives every API
 * fetch the same Authorization / X-CC-Session-Kind headers and one
 * refresh-and-retry on 401, without touching 28 call sites. Idempotent.
 */
function _installNativeFetchBridge() {
  if (_nativeFetch || typeof window === "undefined" || typeof window.fetch !== "function") return;
  _nativeFetch = window.fetch.bind(window);
  const withAuth = (init, token) => {
    const headers = new Headers(init?.headers || {});
    if (token) headers.set("Authorization", `Bearer ${token}`);
    headers.set("X-CC-Session-Kind", "mobile");
    return { ...(init || {}), headers };
  };
  window.fetch = async (input, init) => {
    const url = typeof input === "string" ? input : input?.url;
    if (!isApiRequestUrl(url) || AUTH_ENDPOINTS_RE.test(url)) return _nativeFetch(input, init);
    const res = await _nativeFetch(input, withAuth(init, _cachedAccess));
    if (res.status !== 401 || init?._skipAuthRetry) return res;
    try { await _singleFlightRefresh(); } catch { return res; }
    return _nativeFetch(input, { ...withAuth(init, _cachedAccess), _skipAuthRetry: true });
  };
}

/** Idempotent: interceptors are attached once per page lifetime, however many callers await it. */
export function bootGate() {
  if (!isNativePlatform()) return Promise.resolve();
  if (!_bootGatePromise) {
    _bootGatePromise = (async () => {
      await _installIdentityCheck();
      _cachedAccess = await secureAuthStorage.getItem(K_ACCESS);
      _attach();
      _installNativeFetchBridge();
    })().catch((e) => { _bootGatePromise = null; throw e; });
  }
  return _bootGatePromise;
}

function _attach() {
  api.interceptors.request.use((config) => {
    if (!isNativePlatform()) return config;
    if (_cachedAccess) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${_cachedAccess}`;
    }
    config.headers = config.headers || {};
    config.headers["X-CC-Session-Kind"] = "mobile";
    return config;
  });

  api.interceptors.response.use(
    (r) => r,
    async (error) => {
      const cfg = error?.config;
      const status = error?.response?.status;
      const url = cfg?.url || "";
      if (
        status !== 401 ||
        !isNativePlatform() ||
        cfg?._skipAuthRetry ||
        AUTH_ENDPOINTS_RE.test(url)
      ) throw error;
      try {
        await _singleFlightRefresh();
      } catch (e) {
        throw error;
      }
      cfg._skipAuthRetry = true;
      cfg.headers = cfg.headers || {};
      if (_cachedAccess) cfg.headers["Authorization"] = `Bearer ${_cachedAccess}`;
      return api.request(cfg);
    },
  );
}

export async function _singleFlightRefresh_internal() {
  return _singleFlightRefresh();
}

async function _singleFlightRefresh() {
  if (_inflightRefresh) return _inflightRefresh;
  _inflightRefresh = (async () => {
    const gen = _logoutGen;
    // Reuse persisted request_id across restart/retry until rotation
    // succeeds. Written to secure storage BEFORE the network call so a
    // process kill between send and receive still allows recovery.
    let request_id = await secureAuthStorage.getItem(K_PENDING_REQ);
    if (!request_id) {
      request_id = randomB64Url(16);
      await secureAuthStorage.setItem(K_PENDING_REQ, request_id);
    }
    try {
      const refresh = await secureAuthStorage.getItem(K_REFRESH);
      if (!refresh) throw new Error("no_refresh");
      const { data } = await api.post(
        "/api/auth/mobile/refresh",
        { refresh_token: refresh, request_id },
        { _skipAuthRetry: true },
      );
      if (data.access_token) {
        if (_logoutGen !== gen) {
          // A sign-out happened while this refresh was in flight. Discard
          // the re-minted pair and keep the device signed out — never
          // resurrect credentials after an explicit logout.
          _cachedAccess = null;
          await secureAuthStorage.removeItem(K_ACCESS);
          await secureAuthStorage.removeItem(K_REFRESH);
          await secureAuthStorage.removeItem(K_PENDING_REQ);
          throw new Error("logout_superseded_refresh");
        }
        _cachedAccess = data.access_token;
        await secureAuthStorage.setItem(K_ACCESS, data.access_token);
      }
      if (data.refresh_token) {
        await secureAuthStorage.setItem(K_REFRESH, data.refresh_token);
      }
      // Success: clear the pending request_id so the next rotation
      // uses a fresh id.
      await secureAuthStorage.removeItem(K_PENDING_REQ);
      if (await _abortIfLoggedOut(gen)) throw new Error("logout_superseded_refresh");
      return data;
    } catch (e) {
      // If the server explicitly rejects (401 replay / invalid), clear
      // storage and log the user out. Network errors preserve
      // K_PENDING_REQ so a subsequent retry recovers the ORIGINAL
      // rotation result from the server-side idempotency cache.
      const status = e?.response?.status;
      if (status === 401) {
        _cachedAccess = null;
        await secureAuthStorage.removeItem(K_ACCESS);
        await secureAuthStorage.removeItem(K_REFRESH);
        await secureAuthStorage.removeItem(K_PENDING_REQ);
        try { window.dispatchEvent(new Event("cc:logout")); } catch { /* */ }
      }
      throw e;
    } finally {
      _inflightRefresh = null;
    }
  })();
  return _inflightRefresh;
}

/**
 * If a sign-out happened while an exchange was in flight, discard the minted
 * pair and keep the device signed out. Returns true when it aborted.
 */
async function _abortIfLoggedOut(gen) {
  if (_logoutGen === gen) return false;
  _cachedAccess = null;
  await secureAuthStorage.removeItem(K_ACCESS);
  await secureAuthStorage.removeItem(K_REFRESH);
  await secureAuthStorage.removeItem(K_PENDING_REQ);
  return true;
}

/**
 * Persist BOTH tokens to secure storage, THEN publish the in-memory bearer.
 * Fails closed: a malformed response (missing either token) or a storage
 * failure on either key clears any partial write and never publishes a bearer.
 * Also honours the logout-generation guard before and after persistence.
 */
async function _persistPairAndPublish(data, gen) {
  if (!data?.access_token || !data?.refresh_token) {
    throw new Error("mobile_exchange_malformed_response");
  }
  if (await _abortIfLoggedOut(gen)) throw new Error("logout_superseded_signin");
  try {
    await secureAuthStorage.setItem(K_ACCESS, data.access_token);
    await secureAuthStorage.setItem(K_REFRESH, data.refresh_token);
  } catch (e) {
    // Partial/failed write — remove both so no half-persisted pair survives a
    // relaunch, and never publish an in-memory bearer that storage can't back.
    _cachedAccess = null;
    try { await secureAuthStorage.removeItem(K_ACCESS); } catch { /* */ }
    try { await secureAuthStorage.removeItem(K_REFRESH); } catch { /* */ }
    throw e;
  }
  if (await _abortIfLoggedOut(gen)) throw new Error("logout_superseded_signin");
  _cachedAccess = data.access_token;   // publish ONLY after both tokens persisted
  return data;
}

export async function handleGoogleMobileHandoff({ id_token, attempt_nonce, attempt_id }) {
  // Reject a result that does not belong to a current, user-initiated sign-in
  // on this device (overlap/ambiguous/cross-launch/expired/older-attempt) BEFORE
  // exchange. `attempt_id` binds consumption to the exact launch attempt.
  const attempt = consumeSignInAttempt("google", { id: attempt_id });
  if (!attempt) {
    throw new Error("unsolicited_or_stale_signin_callback");
  }
  // TKT #274563 · the id_token's nonce (sent as attempt_nonce) MUST equal the
  // nonce this app issued for THIS attempt. Single-use (attempt consumed above).
  if (!attempt.server_nonce || attempt_nonce !== attempt.server_nonce) {
    throw new Error("signin_attempt_mismatch");
  }
  const gen = _logoutGen;
  const { data } = await api.post(
    "/api/auth/mobile/google/exchange",
    { id_token, attempt_nonce, client_initiated_at: new Date().toISOString() },
    { headers: { "X-CC-Device-Id": getDeviceId() }, _skipAuthRetry: true },
  );
  return await _persistPairAndPublish(data, gen);
}

/** Native Apple: ASAuthorization id_token → bearer pair (NOT the web cookie endpoint). */
export async function handleAppleMobileHandoff({ id_token, nonce, display_name }) {
  const gen = _logoutGen;
  const { data } = await api.post(
    "/api/auth/mobile/apple/exchange",
    { id_token, nonce: nonce || null, display_name: display_name || null, client_initiated_at: new Date().toISOString() },
    { _skipAuthRetry: true },
  );
  return await _persistPairAndPublish(data, gen);
}

/** Native Microsoft: backend redeems the PKCE code and mints the bearer pair. */
export async function handleMicrosoftMobileHandoff(payload) {
  const gen = _logoutGen;
  const { data } = await api.post(
    "/api/auth/mobile/microsoft/exchange",
    { ...payload, intent: "signin", client_initiated_at: new Date().toISOString() },
    { _skipAuthRetry: true },
  );
  return await _persistPairAndPublish(data, gen);
}

/** Settings → Connect Microsoft (native): code → id_token only; no session change. */
export async function redeemMicrosoftCodeForLink(payload) {
  const { data } = await api.post("/api/auth/mobile/microsoft/exchange", { ...payload, intent: "link" });
  return data;
}

export async function logoutMobile() {
  // Explicit sign-out: the device MUST end its local session even if the
  // server call fails (401 / offline). Clear credentials in `finally`, then
  // let any server error surface so the UI can report "server-side sign-out
  // not confirmed" without falsely implying the device is still signed in.
  try {
    const { data } = await api.post(
      "/api/auth/mobile/logout", {},
      { headers: { "X-CC-Device-Id": getDeviceId() }, _skipAuthRetry: true },
    );
    return data;
  } finally {
    await clearMobileSession();
  }
}

/** Drop the Keychain pair + in-memory bearer without a server call (server already revoked, e.g. account deletion). */
export async function clearMobileSession() {
  _logoutGen += 1;
  invalidateSignInAttempt();
  _cachedAccess = null;
  // Clear LOCAL credentials FIRST and unconditionally. This must NOT be gated
  // on the native Google SDK cleanup, which may reject OR never settle.
  await secureAuthStorage.removeItem(K_ACCESS);
  await secureAuthStorage.removeItem(K_REFRESH);
  await secureAuthStorage.removeItem(K_PENDING_REQ);
  // Best-effort native SDK sign-out, fire-and-forget: a hung or rejected
  // signOut() can never delay or block the local clear above.
  _bestEffortGoogleSdkSignOut();
}

/** Fire-and-forget Google SDK sign-out; swallows rejection, never awaited by callers. */
function _bestEffortGoogleSdkSignOut() {
  Promise.resolve()
    .then(() => import("./cruiseGoogleAuth"))
    .then((mod) => mod.CruiseGoogleAuth.signOut())
    .catch(() => { /* non-native / plugin missing / SDK error / never-settles */ });
}

/** Test-only boolean diagnostic. NEVER returns token values. */
export async function hasTokens() {
  return {
    access: !!(await secureAuthStorage.getItem(K_ACCESS)),
    refresh: !!(await secureAuthStorage.getItem(K_REFRESH)),
  };
}

// Test hooks
export const __test__ = {
  reset: () => { _cachedAccess = null; _inflightRefresh = null; _logoutGen = 0; _bootGatePromise = null; if (_nativeFetch && typeof window !== "undefined") { window.fetch = _nativeFetch; } _nativeFetch = null; },
  K_ACCESS, K_REFRESH, MARKER_FILE,
};
